import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';

const API = (path) => {
  const token = localStorage.getItem('token');
  return { url: `/api/isf${path}`, headers: { Authorization: `Bearer ${token}` } };
};

const PARTY_TYPES = [
  { code: 'CN', label: 'Consignee' },
  { code: 'BY', label: 'Buyer' },
  { code: 'ST', label: 'Ship To' },
  { code: 'CO', label: 'Consolidator' },
  { code: 'SE', label: 'Seller' },
  { code: 'SF', label: 'Container Stuffing Location' },
  { code: 'MF', label: 'Manufacturer' },
];

const STATUS_COLORS = {
  Matched: '#198754', Accepted: '#198754', Submitted: '#0d6efd',
  Rejected: '#dc3545', Draft: '#6c757d', Transmitted: '#fd7e14',
};

const ISFDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [querying, setQuerying] = useState(false);

  // Main form
  const [form, setForm] = useState({
    isf_type: 'ISF-10', bl_type: 'Ocean Bill of Lading - used for Regular Bills',
    shipment_type: 'Standard or regular filings', transaction_type: 'Complete Transaction',
    activity_code: 'Importer or Broker', status: 'Draft',
    shipper: '', consignee: '', oversea_agent: '',
    isf_importer_code: 'Employer Identification Number (IRS #)', isf_importer_no: '',
    isf_bond_holder: '', isf_bond_holder_id: '', isf_bond_type: 'Continuous', surety_code: '',
    ams_no: '', isf_no: '', mbl_no: '', hbl_no: '', master_scac: '',
    carrier: '', carrier_code: '', vessel: '', voyage: '',
    etd: '', eta: '', port_of_loading: '', port_of_discharge: '',
    office: '', sales: '', op: '', customer_ref: '', po_no: '', memo: '',
    linked_shipment_type: '', linked_shipment_id: '',
    cbp_filer_code: '', cbp_entry_number: '', cbp_check_sum: '',
    customs_status: '', bill_on_file: '', strixsmart_ref_no: '',
    first_submit_date: '', latest_submit_date: '', cbp_accept_date: '', first_matched_date: '',
  });

  // Child tables
  const [tradeParties, setTradeParties] = useState([]);
  const [htsCodes, setHtsCodes] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [references, setReferences] = useState([]);
  const [statusLogs, setStatusLogs] = useState([]);
  const [linkedShipment, setLinkedShipment] = useState(null);

  // Shipment search for linking
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [shipmentResults, setShipmentResults] = useState([]);

  const fetchFiling = useCallback(async () => {
    if (isNew) return;
    try {
      const { url, headers } = API(`/${id}`);
      const res = await axios.get(url, { headers });
      const d = res.data;
      setForm(prev => {
        const f = { ...prev };
        Object.keys(f).forEach(k => { if (d[k] !== undefined && d[k] !== null) f[k] = d[k]; });
        return f;
      });
      setTradeParties(d.trade_parties || []);
      setHtsCodes(d.hts_codes || []);
      setEquipment(d.equipment || []);
      setReferences(d.references || []);
      setStatusLogs(d.status_logs || []);
      setLinkedShipment(d.linked_shipment || null);
    } catch (err) {
      console.error('Failed to fetch ISF:', err);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { fetchFiling(); }, [fetchFiling]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { headers } = API('');
      if (isNew) {
        const res = await axios.post('/api/isf', form, { headers });
        toast.success('ISF created successfully');
        navigate(`/isf/${res.data.id}`, { replace: true });
      } else {
        await axios.put(`/api/isf/${id}`, form, { headers });
        toast.success('ISF saved');
        fetchFiling();
      }
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!window.confirm('Copy this ISF filing?')) return;
    try {
      const { headers } = API('');
      const res = await axios.post(`/api/isf/${id}/copy`, {}, { headers });
      toast.success('ISF copied');
      navigate(`/isf/${res.data.id}`);
    } catch (err) {
      toast.error('Copy failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSubmitToCBP = async () => {
    if (!window.confirm('Submit this ISF to CBP via StrixSmart?')) return;
    setSubmitting(true);
    try {
      // Save first before submitting
      const { headers } = API('');
      await axios.put(`/api/isf/${id}`, form, { headers });

      const res = await axios.post(`/api/isf/${id}/submit`, {}, { headers });
      if (res.data.success) {
        toast.success(`ISF submitted successfully! Ref: ${res.data.referenceNo}`);
        // Auto-query status after a brief delay
        fetchFiling();
        setTimeout(async () => {
          try {
            const statusRes = await axios.post(`/api/isf/${id}/query-status`, {}, { headers });
            if (statusRes.data.success) {
              const cs = statusRes.data.customsStatus || '';
              const bof = statusRes.data.billOnFile || '';
              if (cs) toast.info(`CBP Status: ${cs}${bof ? `, Bill on File: ${bof}` : ''}`);
              fetchFiling();
            }
          } catch (e) { /* silent — user can manually query later */ }
        }, 3000);
      } else {
        toast.error('Submit failed: ' + (res.data.error || 'Unknown error'));
        fetchFiling();
      }
    } catch (err) {
      toast.error('Submit failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleQueryStatus = async () => {
    setQuerying(true);
    try {
      const { headers } = API('');
      const res = await axios.post(`/api/isf/${id}/query-status`, {}, { headers });
      if (res.data.success) {
        const cs = res.data.customsStatus || 'N/A';
        const bof = res.data.billOnFile || 'N/A';
        toast.info(`Customs Status: ${cs} | Bill on File: ${bof}`);
        fetchFiling();
      } else {
        toast.error('Query failed: ' + (res.data.error || 'Unknown'));
      }
    } catch (err) {
      toast.error('Query failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setQuerying(false);
    }
  };

  const handleDeleteRemote = async () => {
    if (!window.confirm('Delete this ISF from CBP/StrixSmart? This cannot be undone.')) return;
    try {
      const { headers } = API('');
      const res = await axios.post(`/api/isf/${id}/delete-remote`, {}, { headers });
      if (res.data.success) {
        toast.success('ISF deleted from CBP');
        fetchFiling();
      } else {
        toast.error('Delete failed: ' + (res.data.error || 'Unknown'));
      }
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // ==================== Shipment Link ====================
  const searchShipments = async (q) => {
    if (!form.linked_shipment_type || !q || q.length < 2) { setShipmentResults([]); return; }
    try {
      const { headers } = API('');
      const res = await axios.get(`/api/isf/search/shipments?type=${form.linked_shipment_type}&q=${q}`, { headers });
      setShipmentResults(res.data);
    } catch (err) { setShipmentResults([]); }
  };

  const handleLinkShipment = async (shipmentId) => {
    try {
      const { headers } = API('');
      await axios.put(`/api/isf/${id}/link`, {
        linked_shipment_type: form.linked_shipment_type, linked_shipment_id: shipmentId,
      }, { headers });
      setShipmentResults([]);
      setShipmentSearch('');
      fetchFiling();
    } catch (err) {
      alert('Link failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUnlink = async () => {
    try {
      const { headers } = API('');
      await axios.put(`/api/isf/${id}/unlink`, {}, { headers });
      fetchFiling();
    } catch (err) {
      alert('Unlink failed');
    }
  };

  // ==================== Trade Party CRUD ====================
  const addTradeParty = async (partyType = 'CN') => {
    if (isNew) return;
    try {
      const label = PARTY_TYPES.find(p => p.code === partyType)?.label || 'Consignee';
      const { headers } = API('');
      await axios.post(`/api/isf/${id}/trade-parties`, {
        party_type: partyType, party_type_label: label,
      }, { headers });
      fetchFiling();
    } catch (err) { alert('Failed to add trade party'); }
  };

  const updateTradeParty = async (partyId, field, value) => {
    try {
      const { headers } = API('');
      const updateData = { [field]: value };
      if (field === 'party_type') {
        updateData.party_type_label = PARTY_TYPES.find(p => p.code === value)?.label || value;
      }
      await axios.put(`/api/isf/${id}/trade-parties/${partyId}`, updateData, { headers });
      fetchFiling();
    } catch (err) { console.error('Update failed:', err); }
  };

  const deleteTradeParty = async (partyId) => {
    try {
      const { headers } = API('');
      await axios.delete(`/api/isf/${id}/trade-parties/${partyId}`, { headers });
      fetchFiling();
    } catch (err) { alert('Delete failed'); }
  };

  // ==================== HTS Code CRUD ====================
  const addHtsCode = async () => {
    if (isNew) return;
    try {
      const { headers } = API('');
      await axios.post(`/api/isf/${id}/hts-codes`, {}, { headers });
      fetchFiling();
    } catch (err) { alert('Failed to add HTS code'); }
  };

  const updateHtsCode = async (htsId, field, value) => {
    try {
      const { headers } = API('');
      await axios.put(`/api/isf/${id}/hts-codes/${htsId}`, { [field]: value }, { headers });
      fetchFiling();
    } catch (err) { console.error('Update failed:', err); }
  };

  const deleteHtsCode = async (htsId) => {
    try {
      const { headers } = API('');
      await axios.delete(`/api/isf/${id}/hts-codes/${htsId}`, { headers });
      fetchFiling();
    } catch (err) { alert('Delete failed'); }
  };

  // ==================== Equipment CRUD ====================
  const addEquipment = async () => {
    if (isNew) return;
    try {
      const { headers } = API('');
      await axios.post(`/api/isf/${id}/equipment`, {}, { headers });
      fetchFiling();
    } catch (err) { alert('Failed to add equipment'); }
  };

  const updateEquipment = async (equipId, field, value) => {
    try {
      const { headers } = API('');
      await axios.put(`/api/isf/${id}/equipment/${equipId}`, { [field]: value }, { headers });
      fetchFiling();
    } catch (err) { console.error('Update failed:', err); }
  };

  const deleteEquipment = async (equipId) => {
    try {
      const { headers } = API('');
      await axios.delete(`/api/isf/${id}/equipment/${equipId}`, { headers });
      fetchFiling();
    } catch (err) { alert('Delete failed'); }
  };

  // ==================== Reference CRUD ====================
  const addReference = async () => {
    if (isNew) return;
    try {
      const { headers } = API('');
      await axios.post(`/api/isf/${id}/references`, {}, { headers });
      fetchFiling();
    } catch (err) { alert('Failed to add reference'); }
  };

  const updateReference = async (refId, field, value) => {
    try {
      const { headers } = API('');
      await axios.put(`/api/isf/${id}/references/${refId}`, { [field]: value }, { headers });
      fetchFiling();
    } catch (err) { console.error('Update failed:', err); }
  };

  const deleteReference = async (refId) => {
    try {
      const { headers } = API('');
      await axios.delete(`/api/isf/${id}/references/${refId}`, { headers });
      fetchFiling();
    } catch (err) { alert('Delete failed'); }
  };

  // Delete selected
  const [selectedParties, setSelectedParties] = useState([]);
  const [selectedHts, setSelectedHts] = useState([]);
  const [selectedEquip, setSelectedEquip] = useState([]);
  const [selectedRefs, setSelectedRefs] = useState([]);

  const deleteSelected = async (type, ids, setSelected) => {
    if (ids.length === 0) return;
    const { headers } = API('');
    for (const itemId of ids) {
      try {
        await axios.delete(`/api/isf/${id}/${type}/${itemId}`, { headers });
      } catch (e) { /* continue */ }
    }
    setSelected([]);
    fetchFiling();
  };

  const toggleSelect = (id, selected, setSelected) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = (items, selected, setSelected) => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map(i => i.id));
  };

  // ==================== Render ====================

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  const statusColor = STATUS_COLORS[form.status] || '#6c757d';

  return (
    <div style={{ padding: '0', maxWidth: '100%' }}>
      {/* ===== Top Header Bar ===== */}
      <div style={{
        background: '#2c3e50', color: '#fff', padding: '10px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        position: 'sticky', top: 0, zIndex: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ cursor: 'pointer', fontSize: '16px', opacity: 0.7 }} onClick={() => navigate('/isf')}>&larr;</span>
          {isNew ? (
            <span style={{ fontSize: '15px', fontWeight: 600 }}>New ISF Filing</span>
          ) : (
            <>
              <span style={{ fontSize: '13px', opacity: 0.7 }}>AMS No.</span>
              <span style={{ fontSize: '15px', fontWeight: 600 }}>{form.ams_no || '—'}</span>
              <span style={{ fontSize: '13px', opacity: 0.7, marginLeft: '8px' }}>ISF No.</span>
              <span style={{ fontSize: '15px', fontWeight: 600 }}>{form.isf_no || '—'}</span>
              <span style={{
                background: statusColor, color: '#fff', padding: '2px 12px',
                borderRadius: '12px', fontSize: '12px', fontWeight: 600, marginLeft: '8px',
              }}>
                STATUS: {form.status}
              </span>
              {form.customs_status && (
                <span style={{
                  background: form.customs_status === 'Accepted' ? '#198754' :
                    form.customs_status === 'Rejected' ? '#dc3545' : '#fd7e14',
                  color: '#fff', padding: '2px 10px', borderRadius: '12px', fontSize: '11px',
                }}>
                  CBP: {form.customs_status}
                </span>
              )}
              {form.bill_on_file && (
                <span style={{
                  background: form.bill_on_file === 'Yes' ? '#198754' : '#dc3545',
                  color: '#fff', padding: '2px 10px', borderRadius: '12px', fontSize: '11px',
                }}>
                  {form.bill_on_file === 'Yes' ? 'AMS BILL MATCHED' : 'AMS BILL NOT MATCHED'}
                </span>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {!isNew && (
            <>
              <button onClick={handleCopy} style={btnStyle('#6c757d')}>Copy</button>
              {form.strixsmart_ref_no && (
                <button onClick={handleDeleteRemote} style={btnStyle('#dc3545')}>Delete from CBP</button>
              )}
              <button onClick={handleQueryStatus} disabled={querying || !form.strixsmart_ref_no}
                style={btnStyle(form.strixsmart_ref_no ? '#17a2b8' : '#6c757d')}>
                {querying ? 'Querying...' : 'Query Status'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ===== Main Content ===== */}
      <div style={{ padding: '16px 20px 100px 20px' }}>

        {/* ===== Basic Information ===== */}
        <SectionHeader title="Basic Information" />
        <div style={gridStyle(4)}>
          <Field label="Office" field="office" form={form} onChange={handleChange} />
          <Field label="B/L Type" field="bl_type" form={form} onChange={handleChange} required
            options={['Ocean Bill of Lading - used for Regular Bills', 'House Bill of Lading']} />
          <Field label="AMS No." field="ams_no" form={form} onChange={handleChange} required />
          <Field label="ISF No." field="isf_no" form={form} onChange={handleChange} />

          <Field label="ISF Type" field="isf_type" form={form} onChange={handleChange} required
            options={['ISF-10', 'ISF-5']} />
          <Field label="Shipment Type" field="shipment_type" form={form} onChange={handleChange} required />
          <Field label="Transaction Type" field="transaction_type" form={form} onChange={handleChange} required
            options={['Complete Transaction']} />
          <FieldTP label="Consignee" field="consignee" form={form} onChange={handleChange} required />

          <FieldTP label="Shipper" field="shipper" form={form} onChange={handleChange} required />
          <FieldTP label="Oversea Agent" field="oversea_agent" form={form} onChange={handleChange} />
          <Field label="ISF Importer Code" field="isf_importer_code" form={form} onChange={handleChange} required
            options={['Employer Identification Number (IRS #)', 'Social Security Number']} />
          <Field label="ISF Importer No." field="isf_importer_no" form={form} onChange={handleChange} required />

          <FieldTP label="ISF Bond Holder" field="isf_bond_holder" form={form} onChange={handleChange} />
          <Field label="ISF Bond Holder Activity Code" field="activity_code" form={form} onChange={handleChange}
            options={['Importer or Broker', 'Importer', 'Broker']} />
          <Field label="ISF Bond Type" field="isf_bond_type" form={form} onChange={handleChange}
            options={['Continuous', 'Single']} />
          <Field label="Carrier Code" field="carrier_code" form={form} onChange={handleChange} />

          <Field label="MB/L No." field="mbl_no" form={form} onChange={handleChange} />
          <Field label="HB/L No." field="hbl_no" form={form} onChange={handleChange} />
          <FieldTP label="Carrier" field="carrier" form={form} onChange={handleChange} />
          <Field label="Vessel" field="vessel" form={form} onChange={handleChange} />
          <Field label="Voyage" field="voyage" form={form} onChange={handleChange} />

          <Field label="ETD" field="etd" type="date" form={form} onChange={handleChange} />
          <Field label="ETA" field="eta" type="date" form={form} onChange={handleChange} />
          <Field label="POL" field="port_of_loading" form={form} onChange={handleChange} />
          <Field label="POD" field="port_of_discharge" form={form} onChange={handleChange} />
        </div>

        {/* ===== Shipment Info ===== */}
        <SectionHeader title="Shipment Info" style={{ marginTop: '20px' }} />
        <div style={{ marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#495057' }}>
          Customer Reference / P.O. No.
        </div>
        <input
          type="text" value={form.po_no || ''} placeholder="Add P.O. here..."
          onChange={e => handleChange('po_no', e.target.value)}
          style={{ ...inputStyle, maxWidth: '400px' }}
        />

        {/* Linked Shipment */}
        {!isNew && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <select value={form.linked_shipment_type || ''}
              onChange={e => handleChange('linked_shipment_type', e.target.value)}
              style={{ ...selectStyle, width: '140px' }}>
              <option value="">Link to...</option>
              <option value="ocean_import">Ocean Import</option>
              <option value="truck">Truck</option>
            </select>
            <div style={{ position: 'relative', width: '260px' }}>
              <input type="text" value={shipmentSearch} placeholder="Search file no / MBL..."
                onChange={e => { setShipmentSearch(e.target.value); searchShipments(e.target.value); }}
                style={inputStyle} disabled={!form.linked_shipment_type}
              />
              {shipmentResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
                  border: '1px solid #ced4da', borderRadius: '4px', maxHeight: '200px', overflow: 'auto', zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,.15)',
                }}>
                  {shipmentResults.map(s => (
                    <div key={s.id} onClick={() => handleLinkShipment(s.id)}
                      style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '12px' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      <strong>{s.file_no}</strong> — {s.mbl_no || 'No MBL'} — {s.status}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {linkedShipment && (
              <span style={{
                background: '#e8f4fd', padding: '5px 12px', borderRadius: '4px', fontSize: '12px',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
              }}>
                Linked: <strong>{linkedShipment.file_no}</strong> — {linkedShipment.mbl_no || ''}
                <span onClick={handleUnlink} style={{ color: '#dc3545', cursor: 'pointer', fontWeight: 700 }}>✕</span>
              </span>
            )}
          </div>
        )}

        {/* ===== Trade Party ===== */}
        <SectionHeader title="Trade Party" style={{ marginTop: '24px' }}>
          <button onClick={() => addTradeParty('CN')} style={addBtnStyle} disabled={isNew}>＋</button>
          <button onClick={() => deleteSelected('trade-parties', selectedParties, setSelectedParties)}
            style={delBtnStyle} disabled={selectedParties.length === 0}>🗑</button>
        </SectionHeader>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th width="30"><input type="checkbox"
                  checked={tradeParties.length > 0 && selectedParties.length === tradeParties.length}
                  onChange={() => toggleSelectAll(tradeParties, selectedParties, setSelectedParties)} /></Th>
                <Th width="145">Type</Th>
                <Th width="180">Name</Th>
                <Th width="200">ISF Submission Name</Th>
                <Th width="120">City</Th>
                <Th width="80">State</Th>
                <Th width="80">Zip Code</Th>
                <Th width="100">Country</Th>
                <Th width="40"></Th>
              </tr>
            </thead>
            <tbody>
              {tradeParties.map(p => (
                <tr key={p.id} style={{ background: selectedParties.includes(p.id) ? '#e8f4fd' : undefined }}>
                  <Td><input type="checkbox" checked={selectedParties.includes(p.id)}
                    onChange={() => toggleSelect(p.id, selectedParties, setSelectedParties)} /></Td>
                  <Td>
                    <select value={p.party_type} onChange={e => updateTradeParty(p.id, 'party_type', e.target.value)}
                      style={{ ...cellSelectStyle, background: partyTypeColor(p.party_type) }}>
                      {PARTY_TYPES.map(pt => <option key={pt.code} value={pt.code}>{pt.label}</option>)}
                    </select>
                  </Td>
                  <Td><TradePartnerAutocomplete value={p.name || ''} onChange={val => updateTradeParty(p.id, 'name', val)} /></Td>
                  <Td><input value={p.isf_submission_name || ''} onChange={e => updateTradeParty(p.id, 'isf_submission_name', e.target.value)} style={cellInput} /></Td>
                  <Td><input value={p.city || ''} onChange={e => updateTradeParty(p.id, 'city', e.target.value)} style={cellInput} /></Td>
                  <Td><input value={p.state || ''} onChange={e => updateTradeParty(p.id, 'state', e.target.value)} style={cellInput} /></Td>
                  <Td><input value={p.zip_code || ''} onChange={e => updateTradeParty(p.id, 'zip_code', e.target.value)} style={cellInput} /></Td>
                  <Td><input value={p.country || ''} onChange={e => updateTradeParty(p.id, 'country', e.target.value)} style={cellInput} /></Td>
                  <Td>
                    <button onClick={() => deleteTradeParty(p.id)} style={rowDeleteBtn}>✕</button>
                  </Td>
                </tr>
              ))}
              {tradeParties.length === 0 && (
                <tr><td colSpan={9} style={{ padding: '12px', textAlign: 'center', color: '#adb5bd', fontSize: '13px' }}>
                  No trade parties added yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ===== HTS Code ===== */}
        <SectionHeader title="HTS Code" style={{ marginTop: '24px' }}>
          <button onClick={addHtsCode} style={addBtnStyle} disabled={isNew}>＋</button>
          <button onClick={() => deleteSelected('hts-codes', selectedHts, setSelectedHts)}
            style={delBtnStyle} disabled={selectedHts.length === 0}>🗑</button>
        </SectionHeader>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th width="30"><input type="checkbox"
                  checked={htsCodes.length > 0 && selectedHts.length === htsCodes.length}
                  onChange={() => toggleSelectAll(htsCodes, selectedHts, setSelectedHts)} /></Th>
                <Th width="120">HTS Code</Th>
                <Th width="260">Manufacturer</Th>
                <Th>Description</Th>
                <Th width="40"></Th>
              </tr>
            </thead>
            <tbody>
              {htsCodes.map(h => (
                <tr key={h.id} style={{ background: selectedHts.includes(h.id) ? '#e8f4fd' : undefined }}>
                  <Td><input type="checkbox" checked={selectedHts.includes(h.id)}
                    onChange={() => toggleSelect(h.id, selectedHts, setSelectedHts)} /></Td>
                  <Td><input value={h.hts_code || ''} onChange={e => updateHtsCode(h.id, 'hts_code', e.target.value)} style={cellInput} /></Td>
                  <Td><TradePartnerAutocomplete value={h.manufacturer || ''} onChange={val => updateHtsCode(h.id, 'manufacturer', val)} /></Td>
                  <Td><input value={h.description || ''} onChange={e => updateHtsCode(h.id, 'description', e.target.value)} style={cellInput} /></Td>
                  <Td><button onClick={() => deleteHtsCode(h.id)} style={rowDeleteBtn}>✕</button></Td>
                </tr>
              ))}
              {htsCodes.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '12px', textAlign: 'center', color: '#adb5bd', fontSize: '13px' }}>
                  No HTS codes added yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ===== Reference ===== */}
        <SectionHeader title="Reference" style={{ marginTop: '24px' }}>
          <button onClick={addReference} style={addBtnStyle} disabled={isNew}>＋</button>
          <button onClick={() => deleteSelected('references', selectedRefs, setSelectedRefs)}
            style={delBtnStyle} disabled={selectedRefs.length === 0}>🗑</button>
        </SectionHeader>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th width="30"><input type="checkbox"
                checked={references.length > 0 && selectedRefs.length === references.length}
                onChange={() => toggleSelectAll(references, selectedRefs, setSelectedRefs)} /></Th>
              <Th width="200">Code</Th>
              <Th>Number</Th>
              <Th width="40"></Th>
            </tr>
          </thead>
          <tbody>
            {references.map(r => (
              <tr key={r.id} style={{ background: selectedRefs.includes(r.id) ? '#e8f4fd' : undefined }}>
                <Td><input type="checkbox" checked={selectedRefs.includes(r.id)}
                  onChange={() => toggleSelect(r.id, selectedRefs, setSelectedRefs)} /></Td>
                <Td><input value={r.ref_code || ''} onChange={e => updateReference(r.id, 'ref_code', e.target.value)} style={cellInput} /></Td>
                <Td><input value={r.ref_number || ''} onChange={e => updateReference(r.id, 'ref_number', e.target.value)} style={cellInput} /></Td>
                <Td><button onClick={() => deleteReference(r.id)} style={rowDeleteBtn}>✕</button></Td>
              </tr>
            ))}
            {references.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: '#adb5bd', fontSize: '13px' }}>
                No references added yet.
              </td></tr>
            )}
          </tbody>
        </table>

        {/* ===== Equipment ===== */}
        <SectionHeader title="Equipment" style={{ marginTop: '24px' }}>
          <button onClick={addEquipment} style={addBtnStyle} disabled={isNew}>＋</button>
          <button onClick={() => deleteSelected('equipment', selectedEquip, setSelectedEquip)}
            style={delBtnStyle} disabled={selectedEquip.length === 0}>🗑</button>
        </SectionHeader>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th width="30"><input type="checkbox"
                checked={equipment.length > 0 && selectedEquip.length === equipment.length}
                onChange={() => toggleSelectAll(equipment, selectedEquip, setSelectedEquip)} /></Th>
              <Th width="200">Code</Th>
              <Th>Container No.</Th>
              <Th width="40"></Th>
            </tr>
          </thead>
          <tbody>
            {equipment.map(eq => (
              <tr key={eq.id} style={{ background: selectedEquip.includes(eq.id) ? '#e8f4fd' : undefined }}>
                <Td><input type="checkbox" checked={selectedEquip.includes(eq.id)}
                  onChange={() => toggleSelect(eq.id, selectedEquip, setSelectedEquip)} /></Td>
                <Td><input value={eq.equipment_code || ''} onChange={e => updateEquipment(eq.id, 'equipment_code', e.target.value)} style={cellInput} /></Td>
                <Td><input value={eq.container_no || ''} onChange={e => updateEquipment(eq.id, 'container_no', e.target.value)} style={cellInput} /></Td>
                <Td><button onClick={() => deleteEquipment(eq.id)} style={rowDeleteBtn}>✕</button></Td>
              </tr>
            ))}
            {equipment.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: '#adb5bd', fontSize: '13px' }}>
                No equipment added yet.
              </td></tr>
            )}
          </tbody>
        </table>

        {/* ===== Status Logs ===== */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '28px' }}>
          {/* User Operation Log */}
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>User Operation Log</div>
            {statusLogs.filter(l => l.log_type === 'user_operation').map(log => (
              <div key={log.id} style={logEntryStyle('#0d6efd')}>
                <div style={logTimeStyle}>{formatLogDate(log.logged_at)}</div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{log.status}</div>
                {log.logged_by && <div style={{ fontSize: '11px', color: '#6c757d' }}>{log.logged_by}</div>}
                {log.message && <div style={{ fontSize: '12px', color: '#495057', marginTop: '2px' }}>{log.message}</div>}
              </div>
            ))}
            {statusLogs.filter(l => l.log_type === 'user_operation').length === 0 && (
              <div style={{ color: '#adb5bd', fontSize: '13px' }}>No operations logged.</div>
            )}
          </div>

          {/* CBP Response Log */}
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>CBP Response Log</div>
            {statusLogs.filter(l => l.log_type === 'cbp_response').map(log => (
              <div key={log.id} style={logEntryStyle('#198754')}>
                <div style={logTimeStyle}>{formatLogDate(log.logged_at)}</div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{log.status}</div>
                {log.message && <div style={{ fontSize: '12px', color: '#495057', marginTop: '2px' }}>{log.message}</div>}
              </div>
            ))}
            {statusLogs.filter(l => l.log_type === 'cbp_response').length === 0 && (
              <div style={{ color: '#adb5bd', fontSize: '13px' }}>No CBP responses yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* ===== Bottom Action Bar (sticky) ===== */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1px solid #dee2e6',
        padding: '10px 20px', display: 'flex', justifyContent: 'center', gap: '12px',
        zIndex: 20, boxShadow: '0 -2px 8px rgba(0,0,0,.08)',
      }}>
        <button onClick={handleSave} disabled={saving} style={{
          background: '#17a2b8', color: '#fff', border: 'none', padding: '8px 32px',
          borderRadius: '4px', fontSize: '14px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={isNew ? async () => { await handleSave(); } : handleSubmitToCBP}
          disabled={submitting || saving} style={{
          background: '#28a745', color: '#fff', border: 'none', padding: '8px 32px',
          borderRadius: '4px', fontSize: '14px', fontWeight: 600,
          cursor: (submitting || saving) ? 'not-allowed' : 'pointer',
          opacity: (submitting || saving) ? 0.7 : 1,
        }}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

// ==================== Shared UI Components ====================

function SectionHeader({ title, children, style }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      marginBottom: '10px', ...style,
    }}>
      <span style={{ fontSize: '14px', fontWeight: 700, color: '#2c3e50' }}>{title}</span>
      {children}
    </div>
  );
}

function Field({ label, field, form, onChange, type = 'text', options, required, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <label style={labelStyle}>
        {required && <span style={{ color: '#dc3545' }}>*</span>}{label}
      </label>
      {options ? (
        <select value={form[field] || ''} onChange={e => onChange(field, e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="">Select...</option>
          {options.map(o => {
            const val = typeof o === 'object' ? o.value : o;
            const lbl = typeof o === 'object' ? o.label : o;
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </select>
      ) : (
        <input type={type} value={form[field] || ''} onChange={e => onChange(field, e.target.value)} style={inputStyle} disabled={disabled} />
      )}
    </div>
  );
}

function FieldTP({ label, field, form, onChange, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <label style={labelStyle}>
        {required && <span style={{ color: '#dc3545' }}>*</span>}{label}
      </label>
      <TradePartnerAutocomplete value={form[field] || ''} onChange={val => onChange(field, val)} />
    </div>
  );
}

function Th({ children, width }) {
  return (
    <th style={{
      background: '#3c5a72', color: '#fff', padding: '7px 8px', textAlign: 'left',
      fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
      ...(width ? { width: `${width}px`, minWidth: `${width}px` } : {}),
    }}>
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td style={{ padding: '4px 6px', borderBottom: '1px solid #e9ecef', verticalAlign: 'middle' }}>{children}</td>;
}

function formatLogDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd}-${yy}  ${hh}:${mi}`;
}

function partyTypeColor(code) {
  const map = {
    CN: '#e3f2fd', BY: '#fce4ec', ST: '#e8f5e9', CO: '#fff3e0',
    SE: '#f3e5f5', SF: '#e0f7fa', MF: '#fff8e1',
  };
  return map[code] || '#f5f5f5';
}

// ==================== Shared Styles ====================

const labelStyle = { fontSize: '11px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px' };
const inputStyle = { padding: '5px 8px', border: '1px solid #ced4da', borderRadius: '3px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const selectStyle = { padding: '5px 8px', border: '1px solid #ced4da', borderRadius: '3px', fontSize: '13px', background: '#fff', width: '100%', boxSizing: 'border-box' };
const cellInput = { padding: '3px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '12px', width: '100%', boxSizing: 'border-box' };
const cellSelectStyle = { padding: '3px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '11px', fontWeight: 600, width: '100%', boxSizing: 'border-box' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '12px' };

const btnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', padding: '5px 14px',
  borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
});

const addBtnStyle = {
  background: '#28a745', color: '#fff', border: 'none', width: '26px', height: '26px',
  borderRadius: '4px', cursor: 'pointer', fontSize: '16px', fontWeight: 700,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
};

const delBtnStyle = {
  background: '#e9ecef', border: 'none', width: '26px', height: '26px',
  borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
};

const rowDeleteBtn = {
  background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer',
  fontSize: '14px', fontWeight: 700, padding: '2px 4px',
};

const gridStyle = (cols) => ({
  display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '10px 14px', marginBottom: '16px',
});

const logEntryStyle = (borderColor) => ({
  padding: '8px 12px', borderLeft: `3px solid ${borderColor}`,
  marginBottom: '8px', background: '#f8f9fa', borderRadius: '0 4px 4px 0',
});

const logTimeStyle = { fontSize: '11px', color: '#6c757d', marginBottom: '2px' };

export default ISFDetail;
